<script setup>
import { ref, onMounted, computed, watch } from 'vue'
import api from '@/api'
import Swal from 'sweetalert2'
import moment from 'moment'
import MembresiaReciboModal from './MembresiaReciboModal.vue'
import { hasPermission } from '@/composables/useAuth'

const canGestionarMembresiasSocios = computed(() => hasPermission('membresias_socios.gestionar'))

const props = defineProps({
    usuarioId: { type: Number, required: true },
    socio: { type: Object, default: null },
})

const formatCurrency = (n) => {
    const value = Number(n ?? 0)
    return new Intl.NumberFormat('es-EC', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2
    }).format(value)
}

const cancelandoId = ref(null);

// ── Recibo ───────────────────────────────────────────────────────────────────
const reciboVisible = ref(false)
const reciboMembresia = ref(null)
const reciboSocio = ref(null)

function abrirRecibo(m) {
    reciboMembresia.value = m
    reciboSocio.value = props.socio ?? {
        id: props.usuarioId,
        nombre: m.usuario?.nombre || m.nombre_usuario || null,
        email: m.usuario?.email || null,
        telefono: m.usuario?.telefono || null,
    }
    reciboVisible.value = true
}

// estado
const membresias = ref([])
const cargando = ref(false)
const creando = ref(false)
const form = ref({
    membresia_id: '',
    fecha_inicio: moment().format('YYYY-MM-DD'),
    estado: 'pendiente',
    tipo_beneficio: 'ninguno',
    descuento_porcentaje: 0,
    motivo_beneficio: ''
})
const membresiasDisponibles = ref([])
const membresiaSeleccionada = computed(() =>
    membresiasDisponibles.value.find((m) => String(m.id) === String(form.value.membresia_id)) || null
)
const precioListaSeleccionado = computed(() => membresiaSeleccionada.value?.precio ?? null)
watch(() => form.value.tipo_beneficio, (tipo) => {
    if (tipo === 'ninguno') {
        form.value.descuento_porcentaje = 0
        form.value.motivo_beneficio = ''
    } else if (tipo === 'cortesia') {
        form.value.descuento_porcentaje = 100
    }
})

async function cargarMembresias() {
    try {
        cargando.value = true;

        const { data } = await api.get(`/membresia-usuarios/usuario/${props.usuarioId}`);

        // Normalizar datos y limitar a 10 registros más recientes
        membresias.value = (data || [])
            .sort((a, b) => new Date(b.fecha_inicio) - new Date(a.fecha_inicio)) // más nuevas primero
            .slice(0, 10) // mostrar solo las 10 más recientes
            .map(m => ({ ...m }));

    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error al cargar membresías:', e);
        Swal.fire('Error', 'No se pudieron cargar las membresías', 'error');
    } finally {
        cargando.value = false;
    }
}


// traer tipos de membresía disponibles

async function cargarTiposMembresia() {
    try {
        const { data } = await api.get('/membresias')

        // Normaliza por si el backend devuelve un objeto o array
        const lista = Array.isArray(data) ? data : data.membresias || []

        // Asegura que cada item tenga id, nombre y precio válidos
        membresiasDisponibles.value = lista.map(m => ({
            id: m.id,
            nombre: m.nombre ?? m.name ?? 'Sin nombre',
            precio: Number(m.precio ?? m.price ?? 0),
        }))
    } catch (e) {
        if (process.env.NODE_ENV !== 'production') console.error('Error al cargar membresías:', e)
        membresiasDisponibles.value = []
    }
}


// crear una nueva membresía
async function crearMembresia() {
    if (!form.value.membresia_id) {
        Swal.fire('Atención', 'Seleccione una membresía', 'warning')
        return
    }

    if (form.value.tipo_beneficio === 'descuento' && (Number(form.value.descuento_porcentaje) < 0 || Number(form.value.descuento_porcentaje) > 100)) {
        Swal.fire('Atención', 'El descuento debe estar entre 0 y 100.', 'warning')
        return
    }
    if (form.value.tipo_beneficio === 'cortesia' && !form.value.motivo_beneficio?.trim()) {
        Swal.fire('Atención', 'El motivo de la cortesía es obligatorio.', 'warning')
        return
    }

    try {
        creando.value = true
        const payload = {
            usuario_id: props.usuarioId,
            membresia_id: form.value.membresia_id,
            fecha_inicio: form.value.fecha_inicio,
            estado: form.value.estado,
            tipo_beneficio: form.value.tipo_beneficio,
            descuento_porcentaje: form.value.tipo_beneficio === 'cortesia' ? 100 : Number(form.value.descuento_porcentaje || 0),
            motivo_beneficio: form.value.tipo_beneficio === 'ninguno' ? undefined : (form.value.motivo_beneficio || undefined)
        }
        await api.post('/membresia-usuarios', payload)
        Swal.fire('Éxito', 'Membresía registrada', 'success')
        await cargarMembresias()
        form.value = {
            membresia_id: '',
            fecha_inicio: moment().format('YYYY-MM-DD'),
            estado: 'pendiente',
            tipo_beneficio: 'ninguno',
            descuento_porcentaje: 0,
            motivo_beneficio: ''
        }
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error(err)
        Swal.fire('Error', err.response?.data?.message || 'No se pudo crear la membresía', 'error')
    } finally {
        creando.value = false
    }
}

async function solicitarBeneficio() {
    const { value: tipo } = await Swal.fire({
        title: 'Beneficio para la renovación',
        input: 'select',
        inputOptions: { ninguno: 'Ninguno', descuento: 'Descuento', cortesia: 'Cortesía' },
        inputValue: 'ninguno',
        showCancelButton: true,
        confirmButtonText: 'Continuar',
        cancelButtonText: 'Cancelar'
    })
    if (!tipo) return null
    if (tipo === 'ninguno') return { tipo_beneficio: tipo, descuento_porcentaje: 0 }
    const { value: porcentaje } = await Swal.fire({
        title: tipo === 'cortesia' ? 'Cortesía' : 'Descuento',
        input: tipo === 'cortesia' ? 'textarea' : 'number',
        inputLabel: tipo === 'cortesia' ? 'Motivo de la cortesía' : 'Porcentaje de descuento (0 a 100)',
        inputValue: tipo === 'cortesia' ? '' : 0,
        inputAttributes: tipo === 'cortesia' ? {} : { min: 0, max: 100, step: 0.01 },
        inputValidator: (value) => {
            if (tipo === 'cortesia') return !value?.trim() && 'El motivo es obligatorio.'
            return (Number(value) < 0 || Number(value) > 100) && 'El descuento debe estar entre 0 y 100.'
        },
        showCancelButton: true,
        confirmButtonText: 'Renovar',
        cancelButtonText: 'Cancelar'
    })
    if (porcentaje === undefined) return null
    return tipo === 'cortesia'
        ? { tipo_beneficio: tipo, descuento_porcentaje: 100, motivo_beneficio: porcentaje.trim() }
        : { tipo_beneficio: tipo, descuento_porcentaje: Number(porcentaje) }
}

// renovar
async function renovar(m) {
    try {
        const { value: ok } = await Swal.fire({
            title: '¿Renovar membresía?',
            text: `${m.membresia.nombre} (${m.estado})`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sí, renovar'
        })
        if (!ok) return

        const beneficio = await solicitarBeneficio()
        if (!beneficio) return
        await api.post(`/membresia-usuarios/${m.usuario_id}/renovar`, { membresia_id: m.membresia_id, ...beneficio })
        Swal.fire('Renovada', 'La membresía fue renovada correctamente', 'success')
        await cargarMembresias()
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error(err)
        Swal.fire('Error', err.response?.data?.message || 'No se pudo renovar', 'error')
    }
}

// cambiar ahora
async function cambiarAhora(m) {
    try {
        const { value: ok } = await Swal.fire({
            title: '¿Cambiar membresía ahora?',
            text: 'Esto activará una nueva membresía inmediatamente.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Sí, cambiar ahora'
        })
        if (!ok) return

        await api.post(`/membresia-usuarios/${m.usuario_id}/cambiar-ahora`, {
            membresia_id: m.membresia_id,
            prorate: false
        })
        Swal.fire('Cambiada', 'Se activó la nueva membresía', 'success')
        await cargarMembresias()
    } catch (err) {
        if (process.env.NODE_ENV !== 'production') console.error(err)
        Swal.fire('Error', err.response?.data?.message || 'No se pudo cambiar', 'error')
    }
}

// cancelar
async function cancelar(m) {
    if (!['activa', 'pendiente'].includes(m.estado)) return

    const { isConfirmed } = await Swal.fire({
        title: 'Cancelar membresía',
        text: `¿Seguro que deseas cancelar la membresía "${m.membresia?.nombre || ''}"?`,
        icon: 'warning',
        confirmButtonText: 'Sí, cancelar',
        cancelButtonText: 'No',
        showCancelButton: true,
        reverseButtons: true,
    })

    if (!isConfirmed) return

    try {
        cancelandoId.value = m.id
        // Llama a tu endpoint de cancelar
        await api.put(`/membresia-usuarios/cancelar/${m.id}`)
        // Refresca la lista o actualiza el registro en memoria
        m.estado = 'cancelada'
        Swal.fire('Cancelada', 'La membresía fue cancelada correctamente.', 'success')
    } catch (e) {
        Swal.fire('Error', e.response?.data?.message || 'No se pudo cancelar la membresía.', 'error')
    } finally {
        cancelandoId.value = null
    }
}


// Suspender
async function suspender(m) {
    const { isConfirmed } = await Swal.fire({
        title: 'Suspender membresía',
        text: 'Se bloqueará el acceso por falta de pago.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sí, suspender',
        cancelButtonText: 'No'
    });
    if (!isConfirmed) return;

    await api.post(`/membresia-usuarios/${m.id}/suspender`, { motivo: 'Falta de pago' });
    Swal.fire('Listo', 'Membresía suspendida.', 'success');
    await cargarMembresias(); // refresca tabla
}

// Reactivar
async function reactivar(m) {
    // m debe ser el objeto de la fila (tiene m.id = membresia_usuario_id)
    if (!m?.id) {
        Swal.fire('Error', 'ID de la membresía inválido', 'error');
        return;
    }

    // 1) Confirmación
    const { isConfirmed } = await Swal.fire({
        title: '¿Reactivar membresía?',
        text: 'Se reactivará el acceso del socio.',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sí, reactivar',
        cancelButtonText: 'Cancelar'
    });
    if (!isConfirmed) return;

    try {
        // 2) (Opcional, pero recomendable) Chequear saldo pendiente antes
        const saldoResp = await api.get(`/pagos-membresia/membresia-usuarios/${m.id}/saldo`);
        const saldo = Number(saldoResp.data?.saldo_pendiente ?? 0);
        if (saldo > 0) {
            await Swal.fire(
                'No se puede reactivar',
                `Aún hay saldo pendiente de $${saldo.toFixed(2)}. Registra el pago o aplica descuento antes de reactivar.`,
                'warning'
            );
            return;
        }

        // 3) Llamar al endpoint del backend

        await api.post(`/membresia-usuarios/${m.id}/reactivar`);

        await cargarMembresias();
        Swal.fire('Listo', 'Membresía reactivada', 'success');
    } catch (err) {
        // Mostrar mensaje real del backend
        const msg =
            err.response?.data?.message ||
            err.response?.data?.error ||
            err.message ||
            'Error desconocido';
        Swal.fire('No se pudo reactivar', msg, 'error');
        if (process.env.NODE_ENV !== 'production') console.error('Reactivate error:', err);
    }
}


// inicializar
onMounted(() => {
    cargarMembresias()
    cargarTiposMembresia()
})
</script>

<template>
    <div>
        <div class="d-flex justify-content-between align-items-center mb-3">
            <h6 class="mb-0">Membresías asignadas</h6>

        </div>

        <!-- Formulario inline -->
        <div v-if="canGestionarMembresiasSocios" class="border rounded p-3 bg-light mb-3">
            <div class="row g-2 align-items-end">
                <div class="col-12 col-sm-6 col-md-4">
                    <label class="form-label mb-2">Membresía</label>
                    <select v-model="form.membresia_id" class="form-select">
                        <option value="">— Seleccione —</option>
                        <option v-for="m in membresiasDisponibles" :key="m.id" :value="m.id">
                            {{ m.nombre }} — ${{ m.precio.toFixed(2) }}
                        </option>
                    </select>
                </div>

                <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-2">Fecha inicio</label>
                    <input type="date" v-model="form.fecha_inicio" class="form-control form-control-sm" />
                </div>

                <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-2">Estado inicial</label>
                    <select v-model="form.estado" class="form-select form-select-sm">
                        <option value="pendiente">Pendiente</option>
                        <option value="activa">Activa</option>
                        <option value="programada">Programada</option>
                    </select>
                </div>

                <div class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-2">Tipo de beneficio</label>
                    <select v-model="form.tipo_beneficio" class="form-select form-select-sm">
                        <option value="ninguno">Ninguno</option>
                        <option value="descuento">Descuento</option>
                        <option value="cortesia">Cortesía</option>
                    </select>
                </div>

                <div v-if="form.tipo_beneficio === 'descuento'" class="col-12 col-sm-6 col-md-2">
                    <label class="form-label mb-2">Descuento %</label>
                    <input v-model.number="form.descuento_porcentaje" type="number" min="0" max="100" step="0.01" class="form-control form-control-sm" />
                </div>

                <div v-if="form.tipo_beneficio !== 'ninguno'" class="col-12 col-sm-6 col-md-3">
                    <label class="form-label mb-2">Motivo del beneficio</label>
                    <input v-model.trim="form.motivo_beneficio" type="text" class="form-control form-control-sm" :placeholder="form.tipo_beneficio === 'cortesia' ? 'Obligatorio para cortesía' : 'Opcional'" />
                </div>

                <div v-if="precioListaSeleccionado !== null" class="col-12 small text-muted">
                    Precio lista del plan: <strong>{{ formatCurrency(precioListaSeleccionado) }}</strong>.
                    <span v-if="form.tipo_beneficio === 'cortesia'"> Cortesía: descuento 100%; precio final acordado: $0.00.</span>
                    <span v-else> El backend determina y congela el precio final acordado.</span>
                </div>

                <div class="col-12 col-sm-6 col-md-2">
                    <button class="btn btn-primary btn-sm w-100" @click="crearMembresia" :disabled="creando">
                        <i class="ni ni-fat-add me-1"></i>Guardar
                    </button>
                </div>
            </div>
        </div>
        <div v-else class="alert alert-info small">No tienes permiso para gestionar membresías (requiere membresias_socios.gestionar).</div>

        <!-- Tabla de membresías -->
        <div v-if="cargando" class="text-muted">Cargando...</div>

        <div v-else>
            <table class="table table-sm align-middle">
                <thead class="table-light">
                    <tr>
                        <th>ID</th>
                        <th>Membresía</th>
                        <th>Inicio</th>
                        <th>Fin</th>
                        <th>Estado</th>
                        <th>Precio lista</th>
                        <th>Descuento</th>
                        <th>Precio final</th>
                        <th>Saldo</th>
                        <th>Acciones</th>
                    </tr>
                </thead>
                <tbody>
                    <tr v-for="m in membresias" :key="m.id">
                        <td>{{ m.id }}</td>
                        <td>{{ m.membresia?.nombre || '—' }}</td>
                        <td>{{ m.fecha_inicio }}</td>
                        <td>{{ m.fecha_fin || '—' }}</td>
                        <td>
                            <span class="badge" :class="{
                                'bg-success': m.estado === 'activa',
                                'bg-warning': m.estado === 'pendiente',
                                'bg-secondary': m.estado === 'programada',
                                'bg-danger': m.estado === 'vencida',
                                'bg-dark': m.estado === 'cancelada',
                                'bg-warning': m.estado === 'suspendida'
                            }">
                                {{ m.estado }}
                            </span>
                        </td>
                        <td>{{ formatCurrency(m.precio_lista) }}</td>
                        <td>{{ m.descuento_porcentaje ?? 0 }}%<span v-if="m.tipo_beneficio === 'cortesia'"> · Cortesía</span></td>
                        <td>{{ formatCurrency(m.precio_unitario) }}</td>
                        <td>
                            <span :class="{
                                'text-success fw-bold': m.saldo_pendiente === 0,
                                'text-warning fw-bold': m.saldo_pendiente > 0
                            }">
                                {{ formatCurrency(m.saldo_pendiente) }}
                            </span>
                        </td>
                        <td class="text-nowrap text-center">
                            <template v-if="canGestionarMembresiasSocios">
                            <!-- Renovar -->
                            <button class="btn btn-icon btn-outline-success btn-xs me-1"
                                :disabled="!['activa', 'vencida'].includes(m.estado)" @click="renovar(m)"
                                title="Renovar">
                                <i class="fa fa-refresh"></i>

                            </button>

                            <!-- Cambiar ahora -->
                            <button class="btn btn-icon btn-outline-primary btn-xs me-1"
                                :disabled="m.estado !== 'activa'" @click="cambiarAhora(m)" title="Cambiar ahora">
                                <i class="ni ni-curved-next"></i>
                            </button>

                            <!-- Cancelar -->
                            <button class="btn btn-icon btn-outline-danger btn-xs me-1"
                                :disabled="cancelandoId === m.id || !['activa', 'pendiente'].includes(m.estado)"
                                @click="cancelar(m)" title="Cancelar">
                                <i v-if="cancelandoId === m.id" class="ni ni-spinner"></i>
                                <i v-else class="ni ni-fat-remove"></i>
                            </button>

                            <!-- Suspender -->
                            <button class="btn btn-icon btn-outline-warning btn-xs me-1"
                                v-if="['activa', 'pendiente'].includes(m.estado) && Number(m.saldo_pendiente) > 0"
                                @click="suspender(m)" title="Suspender">
                                <i class="ni ni-button-pause"></i>
                            </button>

                            <!-- Reactivar -->
                            <button class="btn btn-icon btn-outline-success btn-xs me-1"
                                v-if="m.estado === 'suspendida'" @click="reactivar(m)" title="Reactivar">
                                <i class="fa fa-play"></i>
                            </button>
                            </template>

                            <!-- Generar Recibo -->
                            <button class="btn btn-icon btn-outline-info btn-xs me-1" @click="abrirRecibo(m)"
                                title="Generar recibo">
                                <i class="ni ni-badge"></i>
                            </button>
                        </td>



                    </tr>
                    <tr v-if="membresias.length === 0">
                        <td colspan="10" class="text-center text-muted">Sin membresías registradas</td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Modal de recibo de membresía -->
    <MembresiaReciboModal :show="reciboVisible" :membresia="reciboMembresia" :socio="reciboSocio"
        @close="reciboVisible = false" />
</template>

<style scoped>
.table td,
.table th {
    font-size: 0.85rem;
}

.btn-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 26px;
    height: 26px;
    border-radius: 6px;
    padding: 0;
    transition: all .15s ease-in-out;
}

.btn-icon i {
    font-size: 1rem;
    /* un pelín más grande para mejor legibilidad */
    line-height: 1;
}

/* xs real, sin padding extra */
.btn-xs {
    padding: 0 !important;
    min-width: 26px;
    min-height: 26px;
}

/* Hovers con relleno e inversión de color */
.btn-outline-success.btn-icon:not(:disabled):hover {
    background-color: #2dce89;
    /* Argon success */
    border-color: #2dce89;
    color: #fff;
}

.btn-outline-primary.btn-icon:not(:disabled):hover {
    background-color: #5e72e4;
    /* Argon primary */
    border-color: #5e72e4;
    color: #fff;
}

.btn-outline-danger.btn-icon:not(:disabled):hover {
    background-color: #f5365c;
    /* Argon danger */
    border-color: #f5365c;
    color: #fff;
}

.btn-outline-info.btn-icon:not(:disabled):hover {
    background-color: #11cdef;
    border-color: #11cdef;
    color: #fff;
}

/* Estado disabled: cursor y opacidad suaves */
button:disabled {
    opacity: .5;
    cursor: not-allowed;
}
</style>
